import React from "react";
import { Link } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "../components/ui/sheet";
import { useCart } from "../context/CartContext";
import { Minus, Plus, X, ShoppingBag, Layers, Sparkles } from "lucide-react";

const WATER_SLUG_TERM = "bacteriostatic";

function getBundleTier(qualified) {
    if (qualified >= 10) return { pct: 30, next: null, prev: 10 };
    if (qualified >= 5) return { pct: 20, next: 10, prev: 5 };
    if (qualified >= 3) return { pct: 10, next: 5, prev: 3 };
    return { pct: 0, next: 3, prev: 0 };
}

export default function CartDrawer() {
    const { items, isOpen, setIsOpen, removeItem, updateQuantity, subtotal } = useCart();

    const qualified = items.reduce((sum, i) => {
        const isWater = (i.name || "").toLowerCase().includes(WATER_SLUG_TERM);
        return isWater ? sum : sum + i.quantity;
    }, 0);
    const tier = getBundleTier(qualified);
    const discountAmount = +(subtotal * (tier.pct / 100)).toFixed(2);
    const total = +(subtotal - discountAmount).toFixed(2);

    // Progress to next tier (0..1)
    let progress = 0;
    if (tier.next) {
        const lower = tier.prev;
        progress = Math.min(1, Math.max(0, (qualified - lower) / (tier.next - lower)));
    } else {
        progress = 1;
    }

    return (
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetContent className="w-full sm:max-w-md bg-white flex flex-col" data-testid="cart-drawer">
                <SheetHeader>
                    <SheetTitle className="font-heading text-2xl text-pep-navy">Your Cart</SheetTitle>
                </SheetHeader>

                {items.length === 0 ? (
                    <div className="flex-1 flex flex-col items-center justify-center text-center px-6" data-testid="cart-empty">
                        <div className="w-16 h-16 rounded-full bg-pep-surface flex items-center justify-center mb-4">
                            <ShoppingBag className="w-6 h-6 text-pep-muted" />
                        </div>
                        <p className="text-pep-navy font-medium mb-2">Your cart is empty</p>
                        <p className="text-pep-muted text-sm mb-6">Add research peptides to get started</p>
                        <Link
                            to="/all"
                            onClick={() => setIsOpen(false)}
                            className="bg-pep-primary text-white rounded-full px-6 py-3 text-sm font-medium hover:bg-pep-navy transition"
                            data-testid="cart-empty-shop-btn"
                        >
                            Browse Products
                        </Link>
                    </div>
                ) : (
                    <>
                        {/* Bundle progress */}
                        <div
                            data-testid="cart-bundle-progress"
                            className={`rounded-2xl p-4 mt-2 border ${
                                tier.pct > 0
                                    ? "bg-pep-navy text-white border-pep-navy"
                                    : "bg-pep-surface border-pep-border/60"
                            }`}
                        >
                            <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2 text-xs uppercase tracking-widest">
                                    <Layers className={`w-3.5 h-3.5 ${tier.pct > 0 ? "text-pep-accent" : "text-pep-primary"}`} />
                                    <span className={tier.pct > 0 ? "text-pep-accent" : "text-pep-navy"}>
                                        Bundle Tier
                                    </span>
                                </div>
                                {tier.pct > 0 && (
                                    <span className="text-xs font-medium bg-pep-accent text-pep-navy px-2 py-0.5 rounded-full" data-testid="cart-bundle-tier-badge">
                                        {tier.pct}% off active
                                    </span>
                                )}
                            </div>
                            <div className={`text-sm leading-relaxed ${tier.pct > 0 ? "text-white/80" : "text-pep-navy"}`}>
                                {tier.pct === 0 && (
                                    <>Add <strong className="text-pep-primary">{tier.next - qualified} more peptide{tier.next - qualified === 1 ? "" : "s"}</strong> to unlock 10% off.</>
                                )}
                                {tier.pct > 0 && tier.next && (
                                    <>Add <strong className="text-pep-accent">{tier.next - qualified} more</strong> to upgrade to <strong>{tier.next === 5 ? "20" : "30"}% off</strong>.</>
                                )}
                                {tier.pct === 30 && (
                                    <span className="flex items-center gap-1.5">
                                        <Sparkles className="w-3.5 h-3.5 text-pep-accent" /> Max bundle discount unlocked.
                                    </span>
                                )}
                            </div>
                            {/* Progress bar */}
                            <div className={`mt-3 h-1.5 rounded-full overflow-hidden ${tier.pct > 0 ? "bg-white/15" : "bg-pep-border/60"}`}>
                                <div
                                    className={`h-full rounded-full transition-all duration-500 ${tier.pct > 0 ? "bg-pep-accent" : "bg-pep-primary"}`}
                                    style={{ width: `${progress * 100}%` }}
                                />
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto py-4 -mx-6 px-6 space-y-3">
                            {items.map((item) => (
                                <div
                                    key={item.product_id}
                                    data-testid={`cart-item-${item.product_id}`}
                                    className="flex gap-4 p-3 rounded-2xl border border-pep-border/50"
                                >
                                    <div className="w-20 h-20 rounded-xl bg-pep-surface overflow-hidden flex-shrink-0">
                                        {item.image && <img src={item.image} alt={item.name} className="w-full h-full object-cover" />}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex justify-between gap-2">
                                            <p className="font-medium text-pep-navy text-sm leading-snug">{item.name}</p>
                                            <button
                                                onClick={() => removeItem(item.product_id)}
                                                className="text-pep-muted hover:text-pep-navy"
                                                data-testid={`cart-remove-${item.product_id}`}
                                                aria-label="Remove"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                        <p className="text-pep-primary font-medium text-sm mt-1">${item.price.toFixed(2)}</p>
                                        <div className="mt-2 inline-flex items-center gap-2 bg-pep-surface rounded-full px-2 py-1">
                                            <button
                                                onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                                                className="w-6 h-6 rounded-full bg-white flex items-center justify-center hover:text-pep-primary"
                                                data-testid={`cart-decrease-${item.product_id}`}
                                                aria-label="Decrease"
                                            >
                                                <Minus className="w-3 h-3" />
                                            </button>
                                            <span className="text-sm w-6 text-center" data-testid={`cart-qty-${item.product_id}`}>{item.quantity}</span>
                                            <button
                                                onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                                                className="w-6 h-6 rounded-full bg-white flex items-center justify-center hover:text-pep-primary"
                                                data-testid={`cart-increase-${item.product_id}`}
                                                aria-label="Increase"
                                            >
                                                <Plus className="w-3 h-3" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <SheetFooter className="border-t border-pep-border/50 pt-4 flex-col sm:flex-col gap-2">
                            <div className="w-full space-y-1">
                                <div className="flex justify-between items-center text-sm">
                                    <span className="text-pep-muted">Subtotal</span>
                                    <span className="text-pep-navy" data-testid="cart-subtotal">${subtotal.toFixed(2)}</span>
                                </div>
                                {tier.pct > 0 && (
                                    <div className="flex justify-between items-center text-sm">
                                        <span className="text-pep-primary">Bundle discount ({tier.pct}%)</span>
                                        <span className="text-pep-primary" data-testid="cart-bundle-discount">−${discountAmount.toFixed(2)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between items-center text-sm">
                                    <span className="text-pep-muted">
                                        Shipping {subtotal >= 250 ? <span className="text-pep-primary">(free over $250!)</span> : <span className="text-xs">($250+ ships free)</span>}
                                    </span>
                                    <span className="text-pep-navy" data-testid="cart-shipping">
                                        {subtotal >= 250 ? "FREE" : "$25.00"}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center pt-2 border-t border-pep-border/50">
                                    <span className="text-pep-navy font-medium">Total</span>
                                    <span className="font-heading text-2xl text-pep-navy font-medium" data-testid="cart-total">
                                        ${(total + (subtotal >= 250 ? 0 : 25)).toFixed(2)}
                                    </span>
                                </div>
                            </div>
                            <Link
                                to="/checkout"
                                onClick={() => setIsOpen(false)}
                                className="bg-pep-primary text-white rounded-full px-6 py-4 text-center font-medium hover:bg-pep-navy transition w-full"
                                data-testid="cart-checkout-btn"
                            >
                                Proceed to Checkout
                            </Link>
                        </SheetFooter>
                    </>
                )}
            </SheetContent>
        </Sheet>
    );
}
